import * as React from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  MousePointerClick,
  Maximize2,
  Minimize2,
  Minus,
  PanelRightClose,
  PanelRightOpen,
  Plus,
  Scan,
  Search,
  Layers3,
} from 'lucide-react';
import type { Mapa3DResposta, Semaforo, VagaMapa } from '@/lib/tipos';
import { Ramo } from '@/componentes/mapa/Ramo';
import { lerEndereco } from '@/componentes/movimentacao/rack';
import { cn, numero } from '@/lib/utils';
import { SEMCOR } from '@/lib/semaforo';
import { urlImagemProduto } from '@wms/shared';
import { ImagemProduto } from '@/componentes/produto/ImagemProduto';

/**
 * Elevação do rack de uma RUA (corredor), no dialeto do operador:
 *   RUA (corredor) → LOTE (módulo) → ANDAR (box/nível).
 * Endereço: "Rua A · Lote 5 · Andar 3".
 *
 * Iteração 3 — o pedido foi literal: "a planta 3D da rua, aproveite mais o
 * espaço da tela, está muito pequena quando vou no segundo nível". Antes a
 * célula tinha tamanho FIXO (34×26 px), então rua curta deixava metade da
 * tela vazia e rua longa estourava em barra de rolagem. Agora:
 *
 *  - AJUSTE À TELA: a célula é calculada a partir do espaço real disponível
 *    (ResizeObserver), tanto em largura (lotes) quanto em altura (andares).
 *  - ZOOM: multiplicador sobre esse tamanho, com Ctrl+roda do mouse e botões.
 *  - PAINEL RETRÁTIL: fechando o painel lateral o rack ganha ~360 px.
 *  - TELA CHEIA: o rack ocupa o monitor inteiro (F11 do navegador não some
 *    com o cabeçalho do app; este botão sim).
 *  - RÉGUA DA RUA: minimapa de ocupação por lote, clicável, no rodapé.
 */

// Duas colunas fixas à esquerda: o ANDAR uma vez só e o BOX de cada
// profundidade ramificando dali. A SOMA é o que o ajuste automático desconta
// da largura disponível, então a conta de caber na tela não muda.
const LARGURA_ANDAR = 52;
const LARGURA_BOX = 30;
const LARGURA_ROTULO = LARGURA_ANDAR + LARGURA_BOX;
const ALTURA_CABECALHO_LOTES = 26;
const RESPIRO = 24; // padding do palco

// Limites do ajuste automático. LOTE_MIN existe porque abaixo de ~18 px a
// vaga vira um risco: rua de 130 lotes prefere rolar na horizontal (a régua
// do rodapé orienta) a virar um código de barras ilegível.
const LOTE_MIN = 18;
const LOTE_MAX = 96;
const VAGA_MIN = 10;
const VAGA_MAX = 84;
// Uma vaga de porta-palete é ~1,2 m de largura por ~1,6 m de altura: um pouco
// mais alta que larga. Fora dessa faixa a célula deixa de parecer uma vaga.
const RAZAO_MAX = 2.2;
const RAZAO_MIN = 0.55;
/** Vão vertical entre duas faixas da rua dobrada. */
const VAO_FAIXA = 18;
const CHAVE_PAINEL = 'wms.rack.painel';

export interface PropsRack3D {
  dados: Mapa3DResposta;
  /** SKU realçado: suas vagas acendem, o resto esmaece (rua continua visível). */
  realceSku?: number | null | undefined;
  selecionado?: number | null | undefined;
  aoSelecionar: (localizador: number | null) => void;
  /** Volta para a planta do CD (1º nível). */
  aoVoltar?: (() => void) | undefined;
  /** Todas as ruas do centro — permite pular de rua sem voltar à planta. */
  ruas?: readonly string[] | undefined;
  aoTrocarRua?: ((rua: string) => void) | undefined;
  /**
   * Há uma rua chegando: o desenho na tela ainda é o da anterior.
   *
   * A rua nova entra sem desmontar o componente (ver `useMapa3D`), o que
   * preserva tela cheia, painel e zoom — mas por um instante o título diz uma
   * rua e o rack mostra outra. O aviso existe para esse instante.
   */
  atualizando?: boolean | undefined;
  /**
   * Marcação para a etiqueta do localizador: com `aoMarcar`, o clique na vaga
   * abre o detalhe como sempre E marca (ou desmarca) a vaga — o usuário sai
   * clicando e imprime no fim. Sem modo para ligar.
   */
  marcados?: ReadonlySet<number> | undefined;
  aoMarcar?: ((localizador: number) => void) | undefined;
  /** O que fazer com os marcados (Imprimir / limpar): vai na barra do rack. */
  acoesMarcados?: React.ReactNode;
}

interface Dica {
  vaga: VagaMapa;
  x: number;
  y: number;
}

export function Rack3D({
  dados,
  realceSku,
  selecionado,
  aoSelecionar,
  aoVoltar,
  ruas,
  aoTrocarRua,
  atualizando = false,
  marcados,
  aoMarcar,
  acoesMarcados,
}: PropsRack3D): JSX.Element {
  const raizRef = React.useRef<HTMLDivElement | null>(null);
  const palcoRef = React.useRef<HTMLDivElement | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  const [dica, definirDica] = React.useState<Dica | null>(null);
  const [loteSel, definirLoteSel] = React.useState<number | null>(null);
  // Padrão ABERTO: os indicadores da direita são o contexto do que se está
  // olhando. Quem quer mais rack fecha — e a escolha fica lembrada, para não
  // ter de fechar de novo a cada rua.
  const [painelAberto, definirPainelAberto] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(CHAVE_PAINEL) !== '0';
    } catch {
      return true;
    }
  });

  React.useEffect(() => {
    try {
      localStorage.setItem(CHAVE_PAINEL, painelAberto ? '1' : '0');
    } catch {
      /* storage bloqueado */
    }
  }, [painelAberto]);
  // 'dobrada'  = a rua quebra em faixas empilhadas, como texto quebra linha.
  //              Mantém a vaga legível E numerada, e a rolagem vira vertical.
  // 'continua' = a rua é uma tira só; fiel à geometria, rola na horizontal.
  const [modo, definirModo] = React.useState<'dobrada' | 'continua'>('dobrada');
  // Faixa visível (0..1) da rua, para a régua do rodapé mostrar onde estamos.
  const [janela, definirJanela] = React.useState({ inicio: 0, fim: 1 });
  const [telaCheia, definirTelaCheia] = React.useState(false);
  const [zoom, definirZoom] = React.useState(1);
  const [medida, definirMedida] = React.useState({ largura: 0, altura: 0 });

  // Reset ao trocar de rua.
  React.useEffect(() => {
    definirDica(null);
    definirLoteSel(null);
    definirZoom(1);
    definirModo('dobrada');
  }, [dados.corredor]);

  // Trocar de modo com zoom aplicado anularia o ajuste — volta para 100%.
  React.useEffect(() => {
    definirZoom(1);
  }, [modo]);

  // --- Geometria da rua -----------------------------------------------------
  const { lotes, andares, profs, grade, ocupacaoPorLote } = React.useMemo(() => {
    const ls = [...new Set(dados.vagas.map((v) => v.moduloOrdem))].sort((a, b) => a - b);
    const as = [...new Set(dados.vagas.map((v) => v.nivel))].sort((a, b) => b - a); // topo→piso
    const ps = [...new Set(dados.vagas.map((v) => v.vaga))].sort((a, b) => a - b);
    const g = new Map<string, VagaMapa>();
    const tot = new Map<number, { n: number; ocup: number }>();
    for (const v of dados.vagas) {
      g.set(`${v.moduloOrdem}:${v.nivel}:${v.vaga}`, v);
      const t = tot.get(v.moduloOrdem) ?? { n: 0, ocup: 0 };
      t.n += 1;
      if (v.saldo > 0) t.ocup += 1;
      tot.set(v.moduloOrdem, t);
    }
    const ocup = new Map<number, number>();
    for (const [lote, t] of tot) ocup.set(lote, t.n > 0 ? (t.ocup / t.n) * 100 : 0);
    return { lotes: ls, andares: as, profs: ps, grade: g, ocupacaoPorLote: ocup };
  }, [dados.vagas]);

  /**
   * O BOX de cada linha e o MÓDULO como estão no cadastro.
   *
   * `nivel` e `vaga` são DERIVADOS do BOX pelo servidor, e ninguém na operação
   * fala esse número: fala o BOX ('601'), que é o que está na etiqueta e no
   * ERP. O módulo idem — '025' com zero à esquerda, não 25.
   */
  const { boxDaLinha, textoDoModulo } = React.useMemo(() => {
    const box = new Map<string, string>();
    const modulo = new Map<number, string>();
    for (const v of dados.vagas) {
      const geo = lerEndereco(v.endereco);
      const chave = `${v.nivel}:${v.vaga}`;
      if (geo.box && !box.has(chave)) box.set(chave, geo.box);
      if (geo.modulo && !modulo.has(v.moduloOrdem)) modulo.set(v.moduloOrdem, geo.modulo);
    }
    return { boxDaLinha: box, textoDoModulo: modulo };
  }, [dados.vagas]);

  // --- Medida do palco (é o que permite ajustar à tela) ---------------------
  React.useLayoutEffect(() => {
    const alvo = scrollRef.current;
    if (!alvo) return;
    const observador = new ResizeObserver((entradas) => {
      const caixa = entradas[0]?.contentRect;
      if (caixa) definirMedida({ largura: caixa.width, altura: caixa.height });
    });
    observador.observe(alvo);
    definirMedida({ largura: alvo.clientWidth, altura: alvo.clientHeight });
    return () => observador.disconnect();
  }, []);

  const linhas = Math.max(1, andares.length * profs.length);
  const colunas = Math.max(1, lotes.length);

  /**
   * Tamanho da vaga e quantos lotes cabem por faixa.
   *
   * No modo DOBRADA procuramos a MAIOR vaga (de LOTE_MAX para baixo) tal que,
   * quebrando a rua em faixas de `porFaixa` lotes, a rua inteira ainda caiba
   * na altura disponível. Isso resolve os dois extremos com a mesma conta:
   * rua de 12 lotes ganha vaga enorme numa faixa só; MC010 (227 lotes) vira
   * 4 faixas de vaga legível e numerada, sem rolagem horizontal nenhuma.
   *
   * Quando nem no tamanho mínimo cabe, aceitamos rolagem — mas VERTICAL, que
   * é a que o usuário já espera de uma página.
   */
  const { larguraBase, alturaBase, lotesPorFaixa } = React.useMemo(() => {
    const dispL = medida.largura - LARGURA_ROTULO - RESPIRO;
    const dispA = medida.altura - RESPIRO;
    if (dispL <= 0 || dispA <= 0) {
      return { larguraBase: 34, alturaBase: 26, lotesPorFaixa: colunas };
    }

    if (modo === 'continua') {
      const w = limitar(dispL / colunas, LOTE_MIN, LOTE_MAX);
      const h = limitar(
        limitar((dispA - ALTURA_CABECALHO_LOTES) / linhas, VAGA_MIN, VAGA_MAX),
        w * RAZAO_MIN,
        w * RAZAO_MAX,
      );
      return { larguraBase: w, alturaBase: h, lotesPorFaixa: colunas };
    }

    for (let w = LOTE_MAX; w >= LOTE_MIN; w -= 1) {
      const porFaixa = Math.floor(dispL / w);
      if (porFaixa < 1) continue;
      const nFaixas = Math.ceil(colunas / porFaixa);
      // Altura que sobra para as células, tirando cabeçalhos e vãos.
      const sobra = dispA - nFaixas * ALTURA_CABECALHO_LOTES - (nFaixas - 1) * VAO_FAIXA;
      const h = sobra / (nFaixas * linhas);
      if (h < VAGA_MIN) continue;
      // A vaga não pode ficar deformada nem quando cabe.
      if (h < w * RAZAO_MIN || h > w * RAZAO_MAX) continue;
      return {
        larguraBase: w,
        alturaBase: Math.min(h, VAGA_MAX),
        lotesPorFaixa: porFaixa,
      };
    }

    // Rua grande demais para a tela: menor vaga legível + rolagem vertical.
    const w = LOTE_MIN;
    return {
      larguraBase: w,
      alturaBase: limitar(w * 1.3, VAGA_MIN, VAGA_MAX),
      lotesPorFaixa: Math.max(1, Math.floor(dispL / w)),
    };
  }, [medida.largura, medida.altura, colunas, linhas, modo]);

  const larguraLote = Math.max(4, Math.round(larguraBase * zoom));
  const alturaVaga = Math.max(6, Math.round(alturaBase * zoom));

  /** A rua quebrada em faixas. Uma só faixa = comportamento antigo. */
  const faixas = React.useMemo(() => {
    const tamanho =
      modo === 'continua' ? colunas : Math.max(1, Math.floor(lotesPorFaixa / zoom) || 1);
    const partes: number[][] = [];
    for (let i = 0; i < lotes.length; i += tamanho) partes.push(lotes.slice(i, i + tamanho));
    return partes.length > 0 ? partes : [[]];
  }, [lotes, lotesPorFaixa, colunas, modo, zoom]);

  const larguraTotal = LARGURA_ROTULO + Math.min(colunas, faixas[0]?.length ?? colunas) * larguraLote;
  const cabeNaLargura = larguraTotal <= medida.largura - RESPIRO;
  const mostrarNumero = larguraLote >= 22 && alturaVaga >= 16;

  // Ao abrir a rua, rola até o primeiro LOTE com estoque. Sem isso, ruas com o
  // começo vazio (ex.: MC004) abrem numa área toda livre e parecem "sem nada".
  React.useEffect(() => {
    const sc = scrollRef.current;
    if (!sc || cabeNaLargura) return;
    const ocupados = new Set(dados.vagas.filter((v) => v.saldo > 0).map((v) => v.moduloOrdem));
    const idx = lotes.findIndex((l) => ocupados.has(l));
    sc.scrollLeft = idx > 2 ? Math.max(0, idx * larguraLote - 40) : 0;
    // Só no carregamento da rua: reagir ao zoom faria a tela "pular".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados.corredor]);

  // Posição do scroll -> faixa visível da rua. É o que torna óbvio que existe
  // conteúdo à direita: a régua do rodapé destaca só o trecho na tela.
  React.useEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    const medir = (): void => {
      const total = sc.scrollWidth - LARGURA_ROTULO;
      if (total <= 0) return definirJanela({ inicio: 0, fim: 1 });
      const inicio = Math.max(0, (sc.scrollLeft - LARGURA_ROTULO) / total);
      const fim = Math.min(1, (sc.scrollLeft + sc.clientWidth - LARGURA_ROTULO) / total);
      definirJanela({ inicio: Math.min(inicio, 1), fim: Math.max(fim, 0) });
    };
    medir();
    sc.addEventListener('scroll', medir, { passive: true });
    return () => sc.removeEventListener('scroll', medir);
  }, [larguraLote, colunas, dados.corredor]);

  // Ctrl + roda = zoom, como em qualquer ferramenta de mapa.
  React.useEffect(() => {
    const alvo = scrollRef.current;
    if (!alvo) return;
    const aoRolar = (e: WheelEvent): void => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      definirZoom((z) => limitar(z * (e.deltaY < 0 ? 1.12 : 0.89), 0.5, 4));
    };
    alvo.addEventListener('wheel', aoRolar, { passive: false });
    return () => alvo.removeEventListener('wheel', aoRolar);
  }, []);

  // Tela cheia de verdade: o app inteiro sai da frente do rack.
  React.useEffect(() => {
    const aoMudar = (): void => definirTelaCheia(document.fullscreenElement === raizRef.current);
    document.addEventListener('fullscreenchange', aoMudar);
    return () => document.removeEventListener('fullscreenchange', aoMudar);
  }, []);

  const alternarTelaCheia = (): void => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void raizRef.current?.requestFullscreen?.();
  };

  const realce = React.useMemo(() => {
    if (realceSku == null) return null;
    const set = new Set<number>();
    for (const v of dados.vagas)
      if (v.produtos.some((p) => p.sku === realceSku)) set.add(v.localizador);
    return set;
  }, [dados.vagas, realceSku]);

  const vagaSelecionada = React.useMemo(
    () => dados.vagas.find((v) => v.localizador === selecionado) ?? null,
    [dados.vagas, selecionado],
  );

  const resumoLote = React.useMemo(() => {
    if (loteSel === null) return null;
    const doLote = dados.vagas.filter((v) => v.moduloOrdem === loteSel);
    if (doLote.length === 0) return null;
    const ocup = doLote.filter((v) => v.saldo > 0);
    const comPct = ocup.filter((v) => v.pct != null);
    const media = comPct.length
      ? Math.round(comPct.reduce((s, v) => s + (v.pct ?? 0), 0) / comPct.length)
      : 0;
    const skus = new Set<number>();
    for (const v of doLote) for (const p of v.produtos) skus.add(p.sku);
    return { lote: loteSel, total: doLote.length, ocupadas: ocup.length, media, skus: skus.size };
  }, [dados.vagas, loteSel]);

  const mostrarDica = (vaga: VagaMapa, e: React.MouseEvent): void => {
    const r = palcoRef.current?.getBoundingClientRect();
    if (!r) return;
    definirDica({ vaga, x: e.clientX - r.left, y: e.clientY - r.top });
  };

  const irParaLote = (lote: number): void => {
    const sc = scrollRef.current;
    if (!sc) return;
    const i = lotes.indexOf(lote);
    if (i < 0) return;
    sc.scrollTo({ left: Math.max(0, i * larguraLote - sc.clientWidth / 2), behavior: 'smooth' });
  };

  // Vizinhas na ordem física da rua, para a paginação da barra.
  const indiceRua = (ruas ?? []).findIndex(
    (r) => r.toUpperCase() === dados.corredor.toUpperCase(),
  );
  const ruaAnterior = indiceRua > 0 ? (ruas ?? [])[indiceRua - 1] : undefined;
  const ruaSeguinte =
    indiceRua >= 0 && indiceRua < (ruas?.length ?? 0) - 1 ? (ruas ?? [])[indiceRua + 1] : undefined;

  // Alt + setas percorre as ruas sem tirar a mão do teclado. Alt evita
  // conflito com a rolagem normal da grade.
  React.useEffect(() => {
    const aoTeclar = (e: KeyboardEvent): void => {
      if (!e.altKey) return;
      if (e.key === 'ArrowLeft' && ruaAnterior) {
        e.preventDefault();
        aoTrocarRua?.(ruaAnterior);
      } else if (e.key === 'ArrowRight' && ruaSeguinte) {
        e.preventDefault();
        aoTrocarRua?.(ruaSeguinte);
      }
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [ruaAnterior, ruaSeguinte, aoTrocarRua]);

  const rotuloAndar = (n: number): string => (n === 0 ? 'Piso' : `Andar ${n}`);
  const pctOcupadas = Math.round(
    (dados.resumo.ocupadas / Math.max(1, dados.resumo.posicoes)) * 100,
  );

  return (
    <div
      ref={raizRef}
      className={cn(
        'relative flex h-full min-h-0 overflow-hidden rounded-2xl border border-borda bg-superficie shadow-cartao',
        telaCheia && 'rounded-none border-0',
      )}
    >
      {/* ---------------------------------------------------------------- */}
      {/* PALCO                                                             */}
      {/* ---------------------------------------------------------------- */}
      <section ref={palcoRef} className="relative flex min-w-0 flex-1 flex-col">
        {/* Barra de ferramentas do rack */}
        <div className="flex h-[52px] shrink-0 items-center gap-2 border-b border-borda px-2.5">
          {aoVoltar ? (
            <button
              type="button"
              onClick={aoVoltar}
              className="flex h-9 items-center gap-1.5 rounded-lg pl-1.5 pr-2.5 text-[12.5px] font-semibold text-textoFraco transition-colors hover:bg-superficie2 hover:text-texto"
            >
              <ChevronLeft className="h-4 w-4" />
              Planta
            </button>
          ) : null}

          {/* Paginação de rua: percorrer o CD sem voltar à planta a cada vez.
              A ordem é a física da rua, não a de ocupação. */}
          <div className="flex items-center">
            <BotaoFerramenta
              rotulo={ruaAnterior ? `Rua anterior (${ruaAnterior})` : 'Primeira rua'}
              onClick={() => ruaAnterior && aoTrocarRua?.(ruaAnterior)}
              desabilitado={!ruaAnterior}
            >
              <ChevronLeft className="h-4 w-4" />
            </BotaoFerramenta>

            <SeletorRua
              atual={dados.corredor}
              ruas={ruas ?? []}
              aoTrocar={aoTrocarRua}
              ocupacao={pctOcupadas}
            />

            <BotaoFerramenta
              rotulo={ruaSeguinte ? `Próxima rua (${ruaSeguinte})` : 'Última rua'}
              onClick={() => ruaSeguinte && aoTrocarRua?.(ruaSeguinte)}
              desabilitado={!ruaSeguinte}
            >
              <ChevronRight className="h-4 w-4" />
            </BotaoFerramenta>

            {indiceRua >= 0 && (ruas?.length ?? 0) > 1 ? (
              <span className="tabular ml-1 hidden text-[11px] font-semibold text-textoSuave lg:inline">
                {indiceRua + 1}/{ruas?.length}
              </span>
            ) : null}
          </div>

          <span className="hidden items-center gap-3 text-[11.5px] text-textoFraco md:flex">
            {atualizando ? (
              <span className="flex items-center gap-1.5 rounded-full bg-primaria-suave px-2 py-0.5 text-[11px] font-semibold text-primaria">
                <span className="h-1.5 w-1.5 animate-pulsoSuave rounded-full bg-primaria" />
                carregando a rua…
              </span>
            ) : null}
            <span className="tabular">
              <b className="font-semibold text-texto">{numero(lotes.length)}</b> lotes
            </span>
            <span className="h-3 w-px bg-borda" />
            <span className="tabular">
              <b className="font-semibold text-texto">{andares.length}</b> andares
            </span>
            {profs.length > 1 ? (
              <>
                <span className="h-3 w-px bg-borda" />
                <span className="tabular">
                  <b className="font-semibold text-texto">{profs.length}</b> vagas de profundidade
                </span>
              </>
            ) : null}
          </span>

          <div className="ml-auto flex items-center gap-1.5">
            {acoesMarcados}
            <div className="flex items-center rounded-lg border border-borda bg-superficie2 p-0.5">
              <BotaoFerramenta
                rotulo="Diminuir"
                onClick={() => definirZoom((z) => limitar(z * 0.85, 0.5, 4))}
              >
                <Minus className="h-3.5 w-3.5" />
              </BotaoFerramenta>
              <button
                type="button"
                onClick={() => definirZoom(1)}
                title="Ajustar à tela (Ctrl + roda do mouse também dá zoom)"
                className="tabular min-w-[46px] px-1 text-[11.5px] font-bold text-textoFraco transition-colors hover:text-primaria"
              >
                {Math.round(zoom * 100)}%
              </button>
              <BotaoFerramenta
                rotulo="Aumentar"
                onClick={() => definirZoom((z) => limitar(z * 1.18, 0.5, 4))}
              >
                <Plus className="h-3.5 w-3.5" />
              </BotaoFerramenta>
            </div>

            {/* Dobrada é o padrão: mantém a vaga legível e numerada. Contínua
                existe para quem quer a rua fiel, numa tira só. */}
            <button
              type="button"
              onClick={() => definirModo((m) => (m === 'dobrada' ? 'continua' : 'dobrada'))}
              title={
                modo === 'dobrada'
                  ? 'Ver a rua como uma tira única (rola na horizontal)'
                  : 'Dobrar a rua em faixas, para caber na tela sem rolar'
              }
              aria-pressed={modo === 'dobrada'}
              className={cn(
                'flex h-9 items-center gap-1.5 rounded-lg border px-2.5 text-[12px] font-semibold transition-colors',
                modo === 'dobrada'
                  ? 'gradiente-marca border-transparent text-white shadow-xs'
                  : 'border-borda bg-superficie text-textoFraco shadow-xs hover:text-primaria',
              )}
            >
              <Scan className="h-4 w-4" />
              <span className="hidden sm:inline">
                {modo === 'dobrada' ? 'Rua dobrada' : 'Rua contínua'}
              </span>
            </button>

            <BotaoFerramenta
              rotulo={telaCheia ? 'Sair da tela cheia' : 'Tela cheia'}
              onClick={alternarTelaCheia}
              contorno
            >
              {telaCheia ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </BotaoFerramenta>

            {/* Com rótulo: como ícone solto ninguém achava, e o painel come
                ~360 px que fazem falta no rack. */}
            <button
              type="button"
              onClick={() => definirPainelAberto((v) => !v)}
              title={
                painelAberto
                  ? 'Recolher o painel de indicadores e alargar o rack'
                  : 'Mostrar de novo os indicadores da rua'
              }
              aria-pressed={!painelAberto}
              className={cn(
                'flex h-9 items-center gap-1.5 rounded-lg border px-2.5 text-[12px] font-semibold transition-colors',
                painelAberto
                  ? 'border-borda bg-superficie text-textoFraco shadow-xs hover:text-primaria'
                  : 'gradiente-marca border-transparent text-white shadow-xs',
              )}
            >
              {painelAberto ? (
                <PanelRightClose className="h-4 w-4" />
              ) : (
                <PanelRightOpen className="h-4 w-4" />
              )}
              <span className="hidden lg:inline">
                {painelAberto ? 'Recolher painel' : 'Mostrar painel'}
              </span>
            </button>
          </div>
        </div>

        {/* Elevação */}
        <div
          ref={scrollRef}
          className="grade-planta min-h-0 flex-1 overflow-auto"
          style={{ background: 'rgb(var(--mapa-fundo))' }}
        >
          <div
            className="flex min-h-full flex-col p-3"
            style={{ gap: VAO_FAIXA, justifyContent: faixas.length === 1 ? 'center' : 'flex-start' }}
          >
            {faixas.map((faixa, fi) => (
            <div
              key={`faixa-${faixa[0] ?? fi}`}
              className={cn('flex', cabeNaLargura && 'justify-center')}
            >
            <div
              className="inline-grid"
              style={{
                gridTemplateColumns: `${LARGURA_ANDAR}px ${LARGURA_BOX}px repeat(${faixa.length}, ${larguraLote}px)`,
              }}
            >
              {/* Canto do cabeçalho: o desenho passa a se identificar sozinho,
                  sem depender do seletor lá em cima, e a régua diz o que é —
                  antes eram dígitos soltos que ninguém adivinha serem módulos. */}
              <div
                className="sticky left-0 top-0 z-30 flex flex-col items-end justify-end pb-1 pr-2 leading-tight"
                style={{
                  height: ALTURA_CABECALHO_LOTES,
                  background: 'rgb(var(--mapa-fundo))',
                  gridColumn: 'span 2',
                }}
              >
                <span className="font-mono text-[10px] font-bold text-primaria">
                  RUA {dados.corredor}
                </span>
                <span className="text-[9px] font-bold uppercase tracking-wider text-textoSuave">
                  {faixas.length > 1
                    ? `módulo ${faixa[0]}–${faixa[faixa.length - 1]}`
                    : 'módulo →'}
                </span>
              </div>
              {faixa.map((l) => {
                const ativo = loteSel === l;
                return (
                  <button
                    key={`h${l}`}
                    type="button"
                    onClick={() => definirLoteSel(ativo ? null : l)}
                    className={cn(
                      'sticky top-0 z-20 flex items-end justify-center pb-1 text-[10px] font-bold transition-colors',
                      ativo ? 'text-primaria' : 'text-textoFraco hover:text-texto',
                    )}
                    style={{ height: ALTURA_CABECALHO_LOTES, background: 'rgb(var(--mapa-fundo))' }}
                  >
                    <span
                      className={cn(
                        'tabular rounded px-1 font-mono',
                        ativo && 'bg-primaria text-white',
                        larguraLote < 16 && 'sr-only',
                        // Múltiplos de 5 em tom mais forte: dá para contar de
                        // relance numa rua de 200 módulos.
                        !ativo && l % 5 === 0 && 'text-texto',
                      )}
                    >
                      {textoDoModulo.get(l) ?? l}
                    </span>
                  </button>
                );
              })}

              {/* Linhas: para cada andar (desc), cada vaga (profundidade) */}
              {andares.map((n, ai) =>
                profs.map((prof, pi) => {
                  const topoDoAndar = pi === 0;
                  return (
                    <React.Fragment key={`${n}:${prof}`}>
                      {/* O ANDAR aparece uma vez, centralizado nas linhas dele:
                          antes 'Andar 6' repetia em 601 e 602, como se fossem
                          dois andares. */}
                      {topoDoAndar ? (
                        <div
                          className="sticky left-0 z-20 flex items-center justify-end pr-2 text-right text-[11px] font-bold text-texto"
                          style={{
                            gridRow: `span ${profs.length}`,
                            background: 'rgb(var(--mapa-fundo))',
                            borderTop: ai > 0 ? '2px solid rgb(var(--cor-borda-forte))' : undefined,
                          }}
                        >
                          {rotuloAndar(n)}
                        </div>
                      ) : null}
                      {/* E o BOX de cada profundidade sai dali pela forquilha. */}
                      <div
                        className="sticky z-20 flex items-center pr-1.5"
                        style={{
                          height: alturaVaga,
                          left: LARGURA_ANDAR,
                          background: 'rgb(var(--mapa-fundo))',
                          borderTop:
                            topoDoAndar && ai > 0
                              ? '2px solid rgb(var(--cor-borda-forte))'
                              : undefined,
                        }}
                      >
                        {profs.length > 1 ? (
                          <Ramo primeiro={topoDoAndar} ultimo={pi === profs.length - 1} />
                        ) : null}
                        <span className="tabular font-mono text-[10px] font-bold text-textoSuave">
                          {boxDaLinha.get(`${n}:${prof}`) ?? ''}
                        </span>
                      </div>

                      {faixa.map((l) => {
                        const v = grade.get(`${l}:${n}:${prof}`);
                        const borderTop =
                          topoDoAndar && ai > 0 ? '2px solid rgb(var(--cor-borda-forte))' : undefined;
                        if (!v) {
                          return (
                            <div key={`${l}:${n}:${prof}`} style={{ height: alturaVaga, borderTop }} />
                          );
                        }
                        const aceso = realce ? realce.has(v.localizador) : true;
                        const dim =
                          (realce != null && !aceso) || (loteSel !== null && v.moduloOrdem !== loteSel);
                        const sel = v.localizador === selecionado;
                        const marcada = marcados?.has(v.localizador) ?? false;
                        const vazio = v.saldo === 0;
                        const cor = SEMCOR[v.semaforo] ?? SEMCOR.sem_medida;
                        const destacada = sel || (realce !== null && aceso);
                        return (
                          <button
                            key={`${l}:${n}:${prof}`}
                            type="button"
                            onMouseEnter={(e) => mostrarDica(v, e)}
                            onMouseMove={(e) => mostrarDica(v, e)}
                            onMouseLeave={() => definirDica(null)}
                            onClick={() => {
                              aoMarcar?.(v.localizador);
                              if (sel) {
                                aoSelecionar(null);
                                definirLoteSel(null);
                              } else {
                                aoSelecionar(v.localizador);
                                definirLoteSel(v.moduloOrdem);
                              }
                            }}
                            // Sem `title`: o balão nativo do navegador aparecia
                            // POR CIMA do cartão de detalhe, um tapando o outro.
                            // O endereço já é a primeira linha do cartão.
                            aria-label={`${v.endereco} — ${
                              v.saldo === 0 ? 'vaga livre' : `${v.saldo} unidades`
                            }`}
                            className="group relative m-[1px] rounded-[4px] transition-[opacity,transform] duration-150 hover:z-10 hover:scale-[1.12]"
                            style={{
                              height: alturaVaga - 2,
                              /*
                                A vaga livre agora tem COR, nao ausencia de
                                cor. Ela era transparente com um contorno
                                fino, o que a fazia sumir no fundo da tela --
                                e vaga livre e justamente o que se procura no
                                mapa quando se vai alocar. O azul tambem a
                                separa do cinza de "sem medida", que era o
                                vizinho mais confundivel.
                              */
                              background: vazio ? SEMCOR.vazio : cor,
                              boxShadow:
                                'inset 0 1px 0 rgb(255 255 255 / 0.28), inset 0 0 0 1px rgb(0 0 0 / 0.1)',
                              opacity: dim ? 0.2 : 1,
                              // Marcada para etiqueta: contorno tracejado, que
                              // não se confunde com o sólido da selecionada.
                              outline: destacada
                                ? '2px solid rgb(var(--cor-primaria))'
                                : marcada
                                  ? '2px dashed rgb(var(--cor-texto))'
                                  : undefined,
                              outlineOffset: destacada || marcada ? '1px' : undefined,
                              borderTopWidth: borderTop ? 2 : undefined,
                            }}
                          >
                            {marcada ? (
                              <span className="pointer-events-none absolute -right-1 -top-1 z-10 h-2.5 w-2.5 rounded-full bg-texto ring-2 ring-superficie" />
                            ) : null}
                            {!vazio && mostrarNumero ? (
                              <span
                                className="tabular pointer-events-none absolute inset-0 flex items-center justify-center text-[9px] font-bold leading-none"
                                style={{ color: v.semaforo === 'medio' ? '#3f2d00' : '#fff' }}
                              >
                                {realce && aceso
                                  ? (v.produtos.find((p) => p.sku === realceSku)?.quantidade ??
                                    v.saldo)
                                  : ''}
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </React.Fragment>
                  );
                }),
              )}
            </div>
            </div>
            ))}
          </div>
        </div>

        {/* Régua da rua (minimapa de ocupação por lote) + legenda */}
        <ReguaDaRua
          corredor={dados.corredor}
          lotes={lotes}
          ocupacao={ocupacaoPorLote}
          janela={modo === 'continua' && !cabeNaLargura ? janela : null}
          loteSel={loteSel}
          aoEscolher={(l) => {
            definirLoteSel(l);
            irParaLote(l);
          }}
        />

        {dica ? (
          <DicaVaga
            dica={dica}
            largura={palcoRef.current?.clientWidth ?? 0}
            altura={palcoRef.current?.clientHeight ?? 0}
          />
        ) : null}
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* PAINEL                                                            */}
      {/* ---------------------------------------------------------------- */}
      <aside
        className={cn(
          'flex min-h-0 shrink-0 flex-col gap-4 overflow-y-auto border-l border-borda bg-superficie2 p-4',
          'transition-[width,transform] duration-300 ease-suave',
          // Em telas largas o painel é uma coluna; abaixo disso vira gaveta
          // sobreposta, para o rack nunca perder largura útil.
          'absolute inset-y-0 right-0 z-30 w-[320px] shadow-forte lg:static lg:shadow-none',
          painelAberto
            ? 'translate-x-0 lg:w-[340px] xl:w-[368px]'
            : 'translate-x-full lg:w-0 lg:translate-x-0 lg:overflow-hidden lg:border-l-0 lg:p-0',
        )}
      >
        {painelAberto ? (
          <>
            <div className="grid grid-cols-2 gap-2.5">
              <KpiRack
                rotulo="Ocupadas"
                valor={`${pctOcupadas}%`}
                nota={`${numero(dados.resumo.ocupadas)} de ${numero(dados.resumo.posicoes)}`}
                barra={pctOcupadas}
              />
              <KpiRack
                rotulo="Ocupação média"
                valor={`${dados.resumo.ocupacaoMediaPct}%`}
                nota="volume das vagas cheias"
                barra={dados.resumo.ocupacaoMediaPct}
              />
              <KpiRack
                rotulo="Vagas livres"
                valor={numero(dados.resumo.livres)}
                nota="prontas p/ alocar"
              />
              <KpiRack
                rotulo="Divergências"
                valor={numero(dados.resumo.divergencias)}
                nota="saldo negativo"
                alerta={dados.resumo.divergencias > 0}
              />
            </div>

            {resumoLote ? (
              <div className="rounded-xl border border-primaria/25 bg-primaria-suave p-3.5 shadow-xs">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-sm font-bold text-primaria">
                    <Layers3 className="h-4 w-4" />
                    Lote {resumoLote.lote}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      definirLoteSel(null);
                      aoSelecionar(null);
                    }}
                    className="rounded text-[11px] font-medium text-textoFraco underline-offset-2 hover:underline"
                  >
                    limpar
                  </button>
                </div>
                <div className="mt-2.5 grid grid-cols-3 gap-2 text-center">
                  <MiniDado valor={`${resumoLote.ocupadas}/${resumoLote.total}`} rotulo="vagas" />
                  <MiniDado valor={`${resumoLote.media}%`} rotulo="médio" />
                  <MiniDado valor={numero(resumoLote.skus)} rotulo="SKUs" />
                </div>
              </div>
            ) : null}

            <div>
              <p className="rotulo mb-2">Localizador selecionado</p>
              <DetalheVaga vaga={vagaSelecionada} />
            </div>

            <div className="mt-auto space-y-2 border-t border-borda pt-3">
              <p className="rotulo">Legenda</p>
              <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                <ItemLegenda cor={SEMCOR.vazio} rotulo="vazia" />
                <ItemLegenda cor={SEMCOR.baixo} rotulo="até 40%" />
                <ItemLegenda cor={SEMCOR.medio} rotulo="40–75%" />
                <ItemLegenda cor={SEMCOR.alto} rotulo="acima 75%" />
                <ItemLegenda cor={SEMCOR.sem_medida} rotulo="sem medida" />
              </div>
              <p className="text-[11px] leading-relaxed text-textoFraco">
                Vaga de{' '}
                <b className="font-semibold text-textoFraco">
                  {dados.dimensoesVaga.larguraM} × {dados.dimensoesVaga.profundidadeM} ×{' '}
                  {dados.dimensoesVaga.alturaM} m
                </b>{' '}
                = {dados.dimensoesVaga.volumeM3} m³. Ocupação = volume do produto × qtd ÷ volume da
                vaga.
              </p>

              {/*
                Medida impossível é o único motivo de um corredor inteiro ficar
                sem porcentagem — e quem vê o corredor não tem como adivinhar
                que a causa está numa tela de Admin. O aviso faz essa ponte.
              */}
              {dados.dimensoesVaga.plausivel ? null : (
                <p className="rounded-lg border border-atencao/40 bg-atencao/10 px-2.5 py-2 text-[11px] leading-relaxed">
                  <b>A medida da vaga deste centro não confere.</b> Uma vaga de{' '}
                  {dados.dimensoesVaga.larguraM} × {dados.dimensoesVaga.profundidadeM} ×{' '}
                  {dados.dimensoesVaga.alturaM} m não comporta nada, então nenhuma posição recebe
                  porcentagem. Parece medida em metros num campo que pede centímetros — corrija em
                  Admin → Porta-palete.
                </p>
              )}
            </div>
          </>
        ) : null}
      </aside>
    </div>
  );
}

// --- Peças -----------------------------------------------------------------

function limitar(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

function BotaoFerramenta({
  children,
  rotulo,
  onClick,
  contorno = false,
  desabilitado = false,
  className,
}: {
  children: React.ReactNode;
  rotulo: string;
  onClick: () => void;
  contorno?: boolean;
  desabilitado?: boolean;
  className?: string | undefined;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={desabilitado}
      title={rotulo}
      aria-label={rotulo}
      className={cn(
        'flex h-9 w-9 items-center justify-center rounded-lg text-textoFraco transition-colors',
        'enabled:hover:bg-superficie3 enabled:hover:text-primaria',
        'disabled:cursor-default disabled:opacity-30',
        contorno && 'border border-borda bg-superficie shadow-xs',
        className,
      )}
    >
      {children}
    </button>
  );
}

/** Troca de rua sem voltar à planta — menos cliques para quem já sabe o destino. */
function SeletorRua({
  atual,
  ruas,
  aoTrocar,
  ocupacao,
}: {
  atual: string;
  ruas: readonly string[];
  aoTrocar?: ((rua: string) => void) | undefined;
  ocupacao: number;
}): JSX.Element {
  const [aberto, definirAberto] = React.useState(false);
  const [termo, definirTermo] = React.useState('');
  const refCaixa = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!aberto) return;
    const aoClicarFora = (e: MouseEvent): void => {
      if (!refCaixa.current?.contains(e.target as Node)) definirAberto(false);
    };
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, [aberto]);

  const filtradas = ruas.filter((r) => r.toLowerCase().includes(termo.trim().toLowerCase()));
  const podeTrocar = aoTrocar !== undefined && ruas.length > 1;

  return (
    <div ref={refCaixa} className="relative">
      <button
        type="button"
        onClick={() => podeTrocar && definirAberto((v) => !v)}
        className={cn(
          'flex h-9 items-center gap-2 rounded-lg pl-2 pr-2.5 transition-colors',
          podeTrocar ? 'hover:bg-superficie2' : 'cursor-default',
        )}
      >
        <span className="gradiente-marca rounded-md px-2 py-1 font-mono text-[12.5px] font-bold leading-none text-white shadow-xs">
          {atual}
        </span>
        <span className="tabular hidden text-[11px] font-semibold text-textoFraco sm:inline">
          {ocupacao}% cheia
        </span>
        {podeTrocar ? <ChevronsUpDown className="h-3.5 w-3.5 text-textoSuave" /> : null}
      </button>

      {aberto ? (
        <div className="absolute left-0 top-full z-40 mt-1.5 w-64 overflow-hidden rounded-xl border border-borda bg-superficie shadow-forte animate-escalar">
          <div className="flex items-center gap-2 border-b border-borda px-3">
            <Search className="h-3.5 w-3.5 shrink-0 text-textoSuave" />
            <input
              autoFocus
              value={termo}
              onChange={(e) => definirTermo(e.target.value)}
              placeholder="Ir para a rua…"
              aria-label="Buscar rua"
              className="min-h-[40px] w-full bg-transparent text-[13px] outline-none placeholder:text-textoSuave"
            />
          </div>
          <div className="max-h-64 overflow-y-auto p-1">
            {filtradas.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-textoFraco">Nenhuma rua.</p>
            ) : (
              filtradas.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => {
                    definirAberto(false);
                    definirTermo('');
                    aoTrocar?.(r);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left font-mono text-[12.5px] font-semibold transition-colors',
                    r === atual ? 'bg-primaria-suave text-primaria' : 'hover:bg-superficie2',
                  )}
                >
                  {r}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Minimapa: a rua inteira em 26 px de altura, com o lote selecionado marcado. */
/**
 * Régua da rua: uma barra por LOTE, altura = ocupação, clicável para filtrar.
 *
 * Ganhou numeração porque sem ela a barra não dizia QUAL lote era — só dava
 * para descobrir pelo balão do sistema. Como 227 números não cabem lado a
 * lado, marcamos de N em N (o passo se ajusta à quantidade de lotes) e o lote
 * sob o cursor aparece por extenso à direita.
 */
function ReguaDaRua({
  corredor,
  lotes,
  ocupacao,
  janela,
  loteSel,
  aoEscolher,
}: {
  corredor: string;
  lotes: readonly number[];
  ocupacao: Map<number, number>;
  /** Faixa visível na tela (0..1), ou `null` quando a rua toda já cabe. */
  janela: { inicio: number; fim: number } | null;
  loteSel: number | null;
  aoEscolher: (lote: number) => void;
}): JSX.Element {
  const [sobreLote, definirSobreLote] = React.useState<number | null>(null);

  // ~14 marcas no máximo: mais que isso os números se encostam.
  const passo = Math.max(1, Math.ceil(lotes.length / 14));
  const emFoco = sobreLote ?? loteSel;
  const pctFoco = emFoco === null ? null : Math.round(ocupacao.get(emFoco) ?? 0);

  return (
    <div className="flex h-[58px] shrink-0 items-center gap-3 border-t border-borda bg-superficie px-3">
      <span className="shrink-0 leading-tight">
        <span className="rotulo block">Rua</span>
        <span className="block font-mono text-[13px] font-bold text-texto">{corredor}</span>
      </span>

      <div className="min-w-0 flex-1">
        <div
          className="relative flex h-[26px] items-end gap-[1px] overflow-hidden rounded-md bg-superficie2 p-1"
          onMouseLeave={() => definirSobreLote(null)}
        >
          {/* Janela do que está na tela: torna visível que a rua continua. */}
          {janela ? (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-y-0 z-10 rounded-[3px] border-2 border-primaria bg-primaria/10"
              style={{
                left: `${janela.inicio * 100}%`,
                width: `${Math.max(2, (janela.fim - janela.inicio) * 100)}%`,
              }}
            />
          ) : null}
          {lotes.map((l) => {
            const pct = ocupacao.get(l) ?? 0;
            const cor =
              pct === 0 ? SEMCOR.vazio : pct < 40 ? SEMCOR.baixo : pct < 75 ? SEMCOR.medio : SEMCOR.alto;
            const aceso = loteSel === null || loteSel === l || sobreLote === l;
            return (
              <button
                key={l}
                type="button"
                aria-label={`Lote ${l}, ${Math.round(pct)}% ocupado`}
                onMouseEnter={() => definirSobreLote(l)}
                onClick={() => aoEscolher(l)}
                className="min-w-[2px] flex-1 rounded-[1px] transition-all"
                style={{
                  height: `${Math.max(14, pct)}%`,
                  background: cor,
                  opacity: aceso ? 1 : 0.35,
                  outline:
                    loteSel === l || sobreLote === l
                      ? '1px solid rgb(var(--cor-primaria))'
                      : undefined,
                }}
              />
            );
          })}
        </div>

        {/* Eixo: número do módulo de N em N, alinhado ao centro da barra. */}
        <div className="relative mt-1 h-[12px] select-none">
          {lotes.map((l, i) =>
            i % passo === 0 || l === loteSel ? (
              <span
                key={`t${l}`}
                className={cn(
                  'tabular absolute top-0 -translate-x-1/2 text-[9.5px] leading-none',
                  l === loteSel ? 'font-bold text-primaria' : 'font-medium text-textoSuave',
                )}
                style={{ left: `${((i + 0.5) / lotes.length) * 100}%` }}
              >
                {l}
              </span>
            ) : null,
          )}
        </div>
      </div>

      <span className="tabular w-[124px] shrink-0 text-right text-[11px] leading-tight text-textoFraco">
        {emFoco !== null ? (
          <>
            <b className="text-[12px] font-bold text-primaria">Lote {emFoco}</b>
            <span className="block">{pctFoco}% ocupado</span>
          </>
        ) : (
          <>
            {numero(lotes.length)} lotes
            {janela ? (
              <span className="block font-semibold text-primaria">
                vendo {Math.round((janela.fim - janela.inicio) * 100)}%
              </span>
            ) : null}
          </>
        )}
      </span>
    </div>
  );
}

function ItemLegenda({
  cor,
  rotulo,
  vazio = false,
}: {
  cor: string;
  rotulo: string;
  vazio?: boolean;
}): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-textoFraco">
      <i
        className="h-3 w-3 rounded-[3px]"
        style={
          vazio
            ? { background: 'transparent', boxShadow: `inset 0 0 0 1px ${cor}` }
            : { background: cor }
        }
      />
      {rotulo}
    </span>
  );
}

function MiniDado({ valor, rotulo }: { valor: string; rotulo: string }): JSX.Element {
  return (
    <div className="rounded-lg bg-superficie/70 py-1.5">
      <p className="tabular text-[15px] font-bold leading-none text-primaria">{valor}</p>
      <p className="mt-0.5 text-[10px] text-textoFraco">{rotulo}</p>
    </div>
  );
}

function KpiRack({
  rotulo,
  valor,
  nota,
  barra,
  alerta,
}: {
  rotulo: string;
  valor: string;
  nota: string;
  barra?: number | undefined;
  alerta?: boolean | undefined;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-borda bg-superficie p-3 shadow-xs">
      <p className="text-[10px] font-bold uppercase tracking-[0.06em] text-textoFraco">{rotulo}</p>
      <p
        className={cn(
          'tabular mt-1.5 text-[23px] font-bold leading-none tracking-[-0.02em]',
          alerta && 'text-status-divergencia',
        )}
      >
        {valor}
      </p>
      <p className="mt-1 text-[10.5px] leading-tight text-textoFraco">{nota}</p>
      {barra !== undefined ? (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-superficie3">
          <span
            className="gradiente-marca block h-full rounded-full transition-[width] duration-500"
            style={{ width: `${Math.max(2, Math.min(100, barra))}%` }}
          />
        </div>
      ) : null}
    </div>
  );
}

function corSemaforo(sem: Semaforo): string {
  return SEMCOR[sem] ?? SEMCOR.sem_medida;
}

function DicaVaga({
  dica,
  largura,
  altura,
}: {
  dica: Dica;
  largura: number;
  altura: number;
}): JSX.Element {
  const { vaga } = dica;
  const pc = vaga.pct == null ? 'sem medida' : `${vaga.pct}%`;
  const cor = corSemaforo(vaga.semaforo);
  const inverter = dica.x > largura - 280;
  // Vaga na metade de baixo do rack: o cartão sobe, senão é cortado pela
  // borda do palco (a régua fica logo abaixo). Mesma lógica do eixo X.
  const subir = altura > 0 && dica.y > altura * 0.5;
  return (
    <div
      className="pointer-events-none absolute z-40 w-[264px] overflow-hidden rounded-xl text-xs leading-relaxed text-white shadow-forte ring-1 ring-white/10"
      style={{
        background: 'linear-gradient(180deg, #1e2436 0%, #141824 100%)',
        left: inverter ? undefined : dica.x + 16,
        right: inverter ? largura - dica.x + 16 : undefined,
        top: subir ? undefined : Math.max(8, dica.y + 16),
        bottom: subir ? Math.max(8, altura - dica.y + 16) : undefined,
        maxHeight: altura > 0 ? Math.max(140, altura - 24) : undefined,
      }}
      role="status"
    >
      <div className="flex items-center gap-2 px-3 pt-2.5">
        <span className="font-mono text-[13px] font-bold tracking-wide text-indigo-200">
          {vaga.endereco}
        </span>
        <span
          className="ml-auto rounded-md px-1.5 py-0.5 text-[11px] font-bold"
          style={{ background: cor, color: vaga.semaforo === 'medio' ? '#3f2d00' : '#fff' }}
        >
          {pc}
        </span>
      </div>
      <p className="flex flex-wrap items-center gap-x-1.5 px-3 pb-1.5 pt-0.5 text-[10.5px] text-white/45">
        {/* LOCALIZADOR é a chave do endereço no PBS: é por ele que a operação
            confere com o ERP. O endereço "25 64 401" é a DESCRICAO. */}
        <span className="rounded bg-white/10 px-1 py-px font-mono text-[10px] font-bold text-white/75">
          LOC {vaga.localizador}
        </span>
        <span>
          Lote {vaga.moduloOrdem} · {vaga.nivel === 0 ? 'Piso' : `Andar ${vaga.nivel}`} · vaga{' '}
          {vaga.vaga}
        </span>
      </p>
      {vaga.saldo === 0 ? (
        <p className="border-t border-white/10 px-3 py-2 text-white/60">
          Vaga livre — pronta para alocação.
        </p>
      ) : (
        <div className="max-h-[220px] overflow-y-auto">
          {vaga.produtos.map((p) => (
            <div key={p.sku} className="flex items-center gap-2 border-t border-white/10 px-3 py-2">
              <img
                src={urlImagemProduto(p.sku)}
                alt=""
                loading="lazy"
                width={40}
                height={40}
                className="h-10 w-10 shrink-0 rounded-md bg-white object-contain"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                }}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[12px] font-medium text-white">{p.descricao}</p>
                <p className="text-[10.5px] text-white/55">
                  SKU {p.sku}
                  {p.ean ? ` · EAN ${p.ean}` : ''}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <b className="tabular text-[14px] text-white">{numero(p.quantidade)}</b>
                <span className="block text-[10px] text-white/55">un</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {vaga.saldo < 0 ? (
        <p className="border-t border-white/10 bg-red-500/10 px-3 py-1.5 font-semibold text-red-300">
          ⚠ saldo negativo ({numero(vaga.saldo)})
        </p>
      ) : null}
    </div>
  );
}

function DetalheVaga({ vaga }: { vaga: VagaMapa | null }): JSX.Element {
  if (!vaga) {
    return (
      <div className="rounded-xl border border-dashed border-bordaForte bg-superficie px-4 py-7 text-center">
        <span className="mx-auto mb-2 grid h-10 w-10 place-items-center rounded-full bg-superficie2 text-textoSuave">
          <MousePointerClick className="h-5 w-5" />
        </span>
        <b className="mb-1 block text-[13px] text-texto">Nenhuma vaga selecionada</b>
        <span className="text-xs text-textoFraco">
          Clique numa vaga do rack para ver produtos, quantidades e ocupação.
        </span>
      </div>
    );
  }
  const cor = corSemaforo(vaga.semaforo);
  const pc = vaga.pct == null ? '—' : `${vaga.pct}%`;
  return (
    <div className="overflow-hidden rounded-xl border border-borda bg-superficie shadow-xs">
      <div className="flex items-center gap-3 p-3.5">
        <div
          className="grid h-[54px] w-[54px] shrink-0 place-items-center rounded-full"
          style={{
            background: `conic-gradient(${cor} ${(vaga.pct ?? 0) * 3.6}deg, rgb(var(--cor-borda)) 0)`,
          }}
        >
          <span className="tabular grid h-[42px] w-[42px] place-items-center rounded-full bg-superficie text-xs font-bold">
            {pc}
          </span>
        </div>
        <div className="min-w-0">
          <p className="truncate font-mono text-[15px] font-bold tracking-tight">{vaga.endereco}</p>
          <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-[11.5px] text-textoFraco">
            <span className="rounded bg-superficie2 px-1.5 py-px font-mono text-[10.5px] font-bold text-texto ring-1 ring-inset ring-borda">
              LOC {vaga.localizador}
            </span>
            <span>
              Lote {vaga.moduloOrdem} · {vaga.nivel === 0 ? 'Piso' : `Andar ${vaga.nivel}`} · vaga{' '}
              {vaga.vaga} · {numero(vaga.saldo)} un
              {vaga.saldo < 0 ? ' · divergência' : ''}
            </span>
          </p>
        </div>
      </div>

      {/*
        OS DOIS VOLUMES, e nao so a porcentagem.
        
        A porcentagem serve para comparar posicoes e nao serve para julgar o
        CADASTRO: "24%" nao distingue pouca mercadoria numa vaga normal de
        mercadoria normal numa vaga grande demais. Foi exatamente essa duvida
        que apareceu no corredor 25 -- posicoes com cargas bem diferentes
        pintando todas de verde. Com os metros cubicos a vista da para
        conferir a medida da vaga contra o galpao sem sair da tela.
      */}
      {vaga.saldo > 0 ? (
        <div className="flex items-baseline justify-between gap-2 border-t border-borda bg-superficie2 px-3.5 py-2">
          <span className="rotulo">Cubagem</span>
          <span className="tabular text-[12.5px]">
            {vaga.volumeOcupadoM3 === null ? (
              <span className="text-textoFraco">sem medida de produto</span>
            ) : (
              <>
                <b>{vaga.volumeOcupadoM3.toLocaleString('pt-BR', { minimumFractionDigits: 3 })}</b>
                <span className="text-textoFraco">
                  {' '}
                  de {vaga.volumeVagaM3.toLocaleString('pt-BR', { minimumFractionDigits: 3 })} m³
                </span>
              </>
            )}
          </span>
        </div>
      ) : null}

      {vaga.produtos.length > 0 ? (
        vaga.produtos.map((p) => (
          <div key={p.sku} className="flex items-center gap-3 border-t border-borda p-3">
            <ImagemProduto sku={p.sku} tamanho={42} alt="" />
            <div className="min-w-0 flex-1">
              <p className="line-clamp-2 text-[12.5px] font-medium leading-snug">{p.descricao}</p>
              <p className="mt-0.5 text-[11px] text-textoFraco">
                SKU {p.sku}
                {p.ean ? ` · EAN ${p.ean}` : ''}
              </p>
            </div>
            <div className="shrink-0 text-right">
              <p className="tabular text-[17px] font-bold leading-none">{numero(p.quantidade)}</p>
              <p className="text-[11px] text-textoFraco">un</p>
              <p className="text-[11px] font-bold" style={{ color: cor }}>
                {p.pct == null ? 'sem medida' : `${p.pct}%`}
              </p>
              {p.volumeM3 === null ? null : (
                <p className="tabular text-[10.5px] text-textoFraco">
                  {p.volumeM3.toLocaleString('pt-BR', { minimumFractionDigits: 3 })} m³
                </p>
              )}
            </div>
          </div>
        ))
      ) : (
        <div className="border-t border-borda p-3 text-xs text-textoFraco">
          Vaga livre — pronta para alocação.
        </div>
      )}
    </div>
  );
}
